/**
 * Organization Payer Claims Config Section
 *
 * Admin CRUD for payer-specific claims configuration per billing entity.
 * Manages payer submitter IDs required by certain payers (e.g., Florida Blue).
 */

import { useCallback, useEffect, useState } from 'react';
import { useBillingEntitiesAdmin } from '@features/billing-entities';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import { usePayerSearch } from '@features/payer-search';
import {
  Add as AddIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { PayerClaimsConfigResponse } from '@store/generated/billing';
import type { PayerResponse } from '@store/generated/payers';
import { useSnackbar } from 'notistack';

import {
  useCreatePayerClaimsConfigMutation,
  useDeletePayerClaimsConfigMutation,
  usePayerClaimsConfigsQuery,
  useUpdatePayerClaimsConfigMutation,
} from '../store/hooks';

// ============================================================================
// Payer Search for Selection (reusable inline)
// ============================================================================

interface PayerSelectSearchProps {
  onSelect: (payer: PayerResponse) => void;
  disabled?: boolean;
}

function PayerSelectSearch({ onSelect, disabled }: PayerSelectSearchProps) {
  const theme = useTheme();
  const {
    searchTerm,
    setSearchTerm,
    payers,
    isLoading,
    isFetching,
  } = usePayerSearch(300);

  const [isOpen, setIsOpen] = useState(false);

  const handleSelect = (payer: PayerResponse) => {
    onSelect(payer);
    setSearchTerm('');
    setIsOpen(false);
  };

  return (
    <Box sx={{ position: 'relative' }}>
      <TextField
        fullWidth
        label="Search Payer"
        placeholder="Type to search payers..."
        value={searchTerm}
        onChange={(e) => {
          setSearchTerm(e.target.value);
          if (e.target.value.length >= 2) setIsOpen(true);
        }}
        onFocus={() => {
          if (searchTerm.length >= 2) setIsOpen(true);
        }}
        disabled={disabled}
       slotProps={{ input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color="action" />
            </InputAdornment>
          ),
          endAdornment: isFetching ? (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ) : null,
        } }}/>
      {isOpen && searchTerm.length >= 2 && (
        <Box
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            mt: 0.5,
            maxHeight: 300,
            overflow: 'auto',
            bgcolor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            boxShadow: theme.shadows[8],
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : payers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
              No payers found
            </Typography>
          ) : (
            payers.map((payer) => (
              <Box
                key={payer.id || payer.stedi_id}
                onClick={() => handleSelect(payer)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.5,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&:hover': { bgcolor: theme.palette.action.hover },
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Avatar
                  src={payer.avatar_url || undefined}
                  alt={payer.display_name}
                  sx={{ width: 32, height: 32 }}
                >
                  {payer.display_name.charAt(0)}
                </Avatar>
                <Typography variant="body2">{payer.display_name}</Typography>
              </Box>
            ))
          )}
        </Box>
      )}
    </Box>
  );
}

// ============================================================================
// Main Component
// ============================================================================

interface OrganizationPayerClaimsConfigSectionProps {
  organizationId: string;
}

export function OrganizationPayerClaimsConfigSection({
  organizationId,
}: OrganizationPayerClaimsConfigSectionProps) {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  // Data
  const { data: configsData, isLoading, refetch } = usePayerClaimsConfigsQuery({ organizationId });
  const configs = configsData?.configs ?? [];
  const { entities } = useBillingEntitiesAdmin(organizationId);

  // Mutations
  const [createConfig, { isLoading: createLoading }] = useCreatePayerClaimsConfigMutation();
  const [updateConfig, { isLoading: updateLoading }] = useUpdatePayerClaimsConfigMutation();
  const [deleteConfig, { isLoading: deleteLoading }] = useDeletePayerClaimsConfigMutation();

  // Dialog states
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<PayerClaimsConfigResponse | null>(null);

  // Create form state
  const [createForm, setCreateForm] = useState({
    billing_entity_id: '',
    payer_uuid: '',
    payer_name: '',
    payer_avatar_url: '',
    payer_submitter_id: '',
    notes: '',
  });

  // Edit form state
  const [editForm, setEditForm] = useState({
    payer_submitter_id: '',
    notes: '',
  });

  const [formError, setFormError] = useState<string | null>(null);

  // Pre-select billing entity if there's only one
  useEffect(() => {
    if (entities.length === 1 && !createForm.billing_entity_id) {
      setCreateForm((prev) => ({ ...prev, billing_entity_id: entities[0].billing_entity_id }));
    }
  }, [entities, createForm.billing_entity_id]);

  // Helpers
  const getEntityName = useCallback(
    (billingEntityId: string) => {
      const entity = entities.find((e) => e.billing_entity_id === billingEntityId);
      return entity?.legal_name ?? billingEntityId;
    },
    [entities],
  );

  // Handlers
  const handleOpenAdd = () => {
    setCreateForm({
      billing_entity_id: entities.length === 1 ? entities[0].billing_entity_id : '',
      payer_uuid: '',
      payer_name: '',
      payer_avatar_url: '',
      payer_submitter_id: '',
      notes: '',
    });
    setFormError(null);
    setAddDialogOpen(true);
  };

  const handleOpenEdit = (config: PayerClaimsConfigResponse) => {
    setSelectedConfig(config);
    setEditForm({
      payer_submitter_id: config.payer_submitter_id ?? '',
      notes: config.notes ?? '',
    });
    setFormError(null);
    setEditDialogOpen(true);
  };

  const handleOpenDelete = (config: PayerClaimsConfigResponse) => {
    setSelectedConfig(config);
    setDeleteDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!createForm.billing_entity_id || !createForm.payer_uuid) {
      setFormError('Billing entity and payer are required');
      return;
    }

    setFormError(null);
    try {
      await createConfig({
        organizationId,
        createPayerClaimsConfigRequest: {
          billing_entity_id: createForm.billing_entity_id,
          payer_uuid: createForm.payer_uuid,
          payer_submitter_id: createForm.payer_submitter_id.trim() || undefined,
          notes: createForm.notes.trim() || undefined,
        },
      }).unwrap();
      enqueueSnackbar('Payer claims config created', { variant: 'success' });
      setAddDialogOpen(false);
      refetch();
    } catch (error) {
      const msg = extractApiErrorMessage(error, 'Failed to create payer claims config');
      setFormError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleUpdate = async () => {
    if (!selectedConfig) return;

    setFormError(null);
    try {
      await updateConfig({
        organizationId,
        configId: selectedConfig.payer_claims_config_id,
        updatePayerClaimsConfigRequest: {
          payer_submitter_id: editForm.payer_submitter_id.trim() || null,
          notes: editForm.notes.trim() || null,
        },
      }).unwrap();
      enqueueSnackbar('Payer claims config updated', { variant: 'success' });
      setEditDialogOpen(false);
      setSelectedConfig(null);
      refetch();
    } catch (error) {
      const msg = extractApiErrorMessage(error, 'Failed to update payer claims config');
      setFormError(msg);
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!selectedConfig) return;

    try {
      await deleteConfig({
        organizationId,
        configId: selectedConfig.payer_claims_config_id,
      }).unwrap();
      enqueueSnackbar('Payer claims config deleted', { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedConfig(null);
      refetch();
    } catch (error) {
      const msg = extractApiErrorMessage(error, 'Failed to delete payer claims config');
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  const handlePayerSelected = (payer: PayerResponse) => {
    setCreateForm((prev) => ({
      ...prev,
      payer_uuid: payer.id ?? '',
      payer_name: payer.display_name,
      payer_avatar_url: payer.avatar_url ?? '',
    }));
  };

  // ============================================================================
  // Render
  // ============================================================================

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Card
      elevation={0}
      sx={{ border: `1px solid ${theme.palette.grey[200]}`, borderRadius: 2 }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Payer Claims Config
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Payer-specific submitter IDs required for claim submission (e.g., Florida Blue)
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            disabled={entities.length === 0}
          >
            Add Config
          </Button>
        </Box>

        {entities.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Add a billing identity first before creating payer claims configs.
          </Alert>
        )}

        {configs.length > 0 ? (
          <TableContainer>
            <Table size="small" aria-label="Payer claims configurations">
              <TableHead>
                <TableRow>
                  <TableCell>Payer</TableCell>
                  <TableCell>Billing Entity</TableCell>
                  <TableCell>Submitter ID</TableCell>
                  <TableCell>Notes</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map((config) => (
                  <TableRow key={config.payer_claims_config_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={config.payer_avatar_url || undefined}
                          alt={config.payer_name || 'Payer'}
                          sx={{ width: 28, height: 28, fontSize: '0.8rem' }}
                        >
                          {(config.payer_name || '?').charAt(0)}
                        </Avatar>
                        <Typography variant="body2">
                          {config.payer_name || config.payer_uuid}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {getEntityName(config.billing_entity_id)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                        {config.payer_submitter_id || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          maxWidth: 250,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {config.notes || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenEdit(config)}
                          aria-label={`Edit config for ${config.payer_name || 'payer'}`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleOpenDelete(config)}
                          aria-label={`Delete config for ${config.payer_name || 'payer'}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : entities.length > 0 ? (
          <Alert severity="info">
            No payer claims configs yet. Click &ldquo;Add Config&rdquo; to create one for payers that require a submitter ID.
          </Alert>
        ) : null}
      </CardContent>

      {/* ================================================================ */}
      {/* Add Config Dialog                                                 */}
      {/* ================================================================ */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth={"sm"}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Add Payer Claims Config</span>
            <IconButton edge="end" onClick={() => setAddDialogOpen(false)} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {/* Billing Entity Selector */}
            <TextField
              select
              fullWidth
              label="Billing Entity"
              value={createForm.billing_entity_id}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, billing_entity_id: e.target.value }))
              }
            >
              {entities.map((entity) => (
                <MenuItem key={entity.billing_entity_id} value={entity.billing_entity_id}>
                  {entity.legal_name} (NPI: {entity.billing_npi})
                </MenuItem>
              ))}
            </TextField>

            {/* Payer Selector */}
            {createForm.payer_uuid ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                }}
              >
                <Avatar
                  src={createForm.payer_avatar_url || undefined}
                  alt={createForm.payer_name}
                  sx={{ width: 32, height: 32 }}
                >
                  {createForm.payer_name.charAt(0)}
                </Avatar>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {createForm.payer_name}
                </Typography>
                <Button
                  size="small"
                  onClick={() =>
                    setCreateForm((prev) => ({
                      ...prev,
                      payer_uuid: '',
                      payer_name: '',
                      payer_avatar_url: '',
                    }))
                  }
                >
                  Change
                </Button>
              </Box>
            ) : (
              <PayerSelectSearch onSelect={handlePayerSelected} />
            )}

            {/* Submitter ID */}
            <TextField
              fullWidth
              label="Payer Submitter ID"
              placeholder="e.g., H0064"
              value={createForm.payer_submitter_id}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, payer_submitter_id: e.target.value }))
              }
              helperText="The payer-assigned submitter identifier from direct enrollment (e.g., Availity)"
            />

            {/* Notes */}
            <TextField
              fullWidth
              label="Notes (optional)"
              placeholder="e.g., From Availity enrollment, March 2026"
              value={createForm.notes}
              onChange={(e) =>
                setCreateForm((prev) => ({ ...prev, notes: e.target.value }))
              }
              multiline
              rows={2}
            />
          </Box>

          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={createLoading || !createForm.billing_entity_id || !createForm.payer_uuid}
            startIcon={createLoading ? <CircularProgress size={20} /> : undefined}
          >
            {createLoading ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================ */}
      {/* Edit Config Dialog                                                */}
      {/* ================================================================ */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth={"sm"}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span>Edit Payer Claims Config</span>
            <IconButton edge="end" onClick={() => setEditDialogOpen(false)} size="small" aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedConfig && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              {/* Read-only context */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  p: 1.5,
                  bgcolor: theme.palette.grey[50],
                  borderRadius: 1,
                }}
              >
                <Avatar
                  src={selectedConfig.payer_avatar_url || undefined}
                  alt={selectedConfig.payer_name || 'Payer'}
                  sx={{ width: 32, height: 32 }}
                >
                  {(selectedConfig.payer_name || '?').charAt(0)}
                </Avatar>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {selectedConfig.payer_name || selectedConfig.payer_uuid}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getEntityName(selectedConfig.billing_entity_id)}
                  </Typography>
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Payer Submitter ID"
                placeholder="e.g., H0064"
                value={editForm.payer_submitter_id}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, payer_submitter_id: e.target.value }))
                }
                helperText="The payer-assigned submitter identifier"
              />

              <TextField
                fullWidth
                label="Notes (optional)"
                value={editForm.notes}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, notes: e.target.value }))
                }
                multiline
                rows={2}
              />
            </Box>
          )}

          {formError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {formError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={updateLoading}
            startIcon={updateLoading ? <CircularProgress size={20} /> : undefined}
          >
            {updateLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================ */}
      {/* Delete Config Dialog                                              */}
      {/* ================================================================ */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullWidth maxWidth={"sm"}>
        <DialogTitle>Delete Payer Claims Config</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the config for{' '}
            <strong>{selectedConfig?.payer_name || 'this payer'}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : undefined}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
